import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import VideoCard from "@/components/feature/VideoCard";
import ScrollTop from "@/components/feature/ScrollTop";
import { useSeo } from "@/hooks/useSeo";
import { videos, categories } from "@/mocks/videos";

export default function Detail() {
  const { slug = "" } = useParams();
  const video = videos.find((v) => v.slug === slug);
  const related = video ? videos.filter((v) => v.category === video.category && v.slug !== video.slug).slice(0, 4) : [];

  useSeo({
    title: video ? `${video.title} - ${video.categoryName} - 吃瓜视频网` : "内容不存在 - 吃瓜视频网",
    description: video ? video.summary : "你访问的内容不存在或已被删除，欢迎返回首页继续吃瓜。",
    keywords: video ? `吃瓜视频网,${video.categoryName},${video.tags.join(",")}` : "吃瓜视频网",
  });

  useEffect(() => {
    if (!video) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: video.title,
      description: video.summary,
      image: [video.cover],
      datePublished: video.date,
      author: { "@type": "Organization", name: video.author },
      publisher: { "@type": "Organization", name: "吃瓜视频网" },
      mainEntityOfPage: `https://xha38.cn/detail/${video.slug}`,
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.textContent = JSON.stringify(schema);
    document.head.appendChild(tag);
    return () => {
      document.head.removeChild(tag);
    };
  }, [video]);

  if (!video) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="mx-auto max-w-3xl px-4 py-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-heading text-2xl text-foreground-950">内容不存在或已被删除</h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的页面可能已下线，欢迎返回首页继续吃瓜。</p>
          <Link to="/" className="inline-flex mt-6 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600">
            返回首页
          </Link>
        </main>
        <Footer />
        <ScrollTop />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === video.category);

  return (
    <div className="min-h-screen">
      <Header />

      <main className="mx-auto max-w-5xl px-4 md:px-6 pt-6">
        {/* breadcrumb */}
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${video.category}`} className="hover:text-primary-600">{video.categoryName}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700 line-clamp-1 max-w-[200px]">{video.title}</span>
        </nav>

        <article>
          <header className="mb-6">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${video.category}`}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-500/10 text-primary-700 text-xs font-bold"
              >
                <i className={category?.icon}></i>{video.categoryName}
              </Link>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-500/15 text-accent-700 text-xs font-bold">
                <i className="ri-price-tag-3-line"></i>{video.tag}
              </span>
            </div>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl text-foreground-950 leading-snug">{video.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-7 h-7 rounded-full bg-secondary-500/15 text-secondary-700 flex items-center justify-center"><i className="ri-user-line"></i></span>
                <span className="text-foreground-700">{video.author}</span>
              </span>
              <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{video.date}</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{video.views} 次观看</span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i>{video.duration}</span>
            </div>
          </header>

          <figure className="rounded-lg overflow-hidden border border-background-200">
            <img src={video.cover} alt={video.title} title={`${video.title} - 吃瓜视频网`} className="w-full aspect-video object-cover" />
          </figure>

          <div className="mt-6 bg-background-100 border border-background-200 rounded-lg p-4 md:p-5">
            <p className="text-sm text-foreground-700 leading-relaxed"><strong className="text-foreground-950">内容简介：</strong>{video.summary}</p>
          </div>

          <div className="mt-6 space-y-5">
            {video.content.map((p, i) => (
              <p key={i} className="text-[15px] leading-8 text-foreground-800">{p}</p>
            ))}
          </div>

          <footer className="mt-8 flex flex-wrap items-center gap-2 pt-6 border-t border-background-200">
            <span className="text-sm text-foreground-500 mr-1">标签：</span>
            {video.tags.map((t) => (
              <Link
                key={t}
                to={`/search?q=${encodeURIComponent(t)}`}
                className="px-3 py-1 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
              >
                #{t}
              </Link>
            ))}
          </footer>
        </article>

        {/* related */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-2xl text-foreground-950 flex items-center gap-2 mb-6">
              <span className="w-1.5 h-6 rounded bg-primary-500"></span>相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((v) => (
                <VideoCard key={v.slug} video={v} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
      <ScrollTop />
    </div>
  );
}